import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';

interface PrimaryContact {
  type: 'email' | 'phone';
  masked: string;
}

interface ElevationStatus {
  elevated: boolean;
  expires_at: string | null;
  has_totp: boolean;
  primary_contact: PrimaryContact | null;
}

interface ElevationState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  elevated: boolean;
  expiresAt: string | null;
  hasTotp: boolean;
  primaryContact: PrimaryContact | null;
  error: string | null;
}

const initialState: ElevationState = {
  status: 'idle',
  elevated: false,
  expiresAt: null,
  hasTotp: false,
  primaryContact: null,
  error: null,
};

export const fetchElevationStatus = createAsyncThunk(
  'dataVaultElevation/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get<ElevationStatus>(
        'data-vault/elevation/'
      );
      return data;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(err?.response?.data?.detail ?? 'error');
    }
  }
);

const slice = createSlice({
  name: 'dataVaultElevation',
  initialState,
  reducers: {
    resetElevation: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchElevationStatus.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchElevationStatus.fulfilled, (state, action) => {
        state.status = 'ready';
        state.elevated = action.payload.elevated;
        state.expiresAt = action.payload.expires_at;
        state.hasTotp = action.payload.has_totp;
        state.primaryContact = action.payload.primary_contact;
        state.error = null;
      })
      .addCase(fetchElevationStatus.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload as string;
      });
  },
});

export const { resetElevation } = slice.actions;

// Selectors
export const selectIsElevated = (state: {
  dataVaultElevation: ElevationState;
}): boolean => state.dataVaultElevation.elevated;

export const selectElevationStatus = (state: {
  dataVaultElevation: ElevationState;
}): ElevationState['status'] => state.dataVaultElevation.status;

export const selectHasTotp = (state: {
  dataVaultElevation: ElevationState;
}): boolean => state.dataVaultElevation.hasTotp;

export const selectPrimaryContactMasked = (state: {
  dataVaultElevation: ElevationState;
}): PrimaryContact | null => state.dataVaultElevation.primaryContact;

export const selectElevationExpiresAt = (state: {
  dataVaultElevation: ElevationState;
}): string | null => state.dataVaultElevation.expiresAt;

export default slice.reducer;
